import { z } from 'zod';

/** A calendar period: a year + 1-based month. */
export interface Period {
  year: number;
  month: number;
}

export const periodYearSchema = z.number().int().min(2000).max(2100);
export const periodMonthSchema = z.number().int().min(1).max(12);

export const periodSchema = z.object({
  year: periodYearSchema,
  month: periodMonthSchema,
});

/** Total month index, used to compare/subtract periods deterministically. */
export function periodIndex(p: Period): number {
  return p.year * 12 + (p.month - 1);
}

export function comparePeriods(a: Period, b: Period): number {
  return periodIndex(a) - periodIndex(b);
}

/**
 * A statement period too far in the FUTURE to ingest. Statements describe months
 * that have (at least) started; scoring a future month poisons deltas/attrition —
 * a sparse advance-only "month" reads as mass member drop-off. One month of grace
 * covers carriers that post next month's statement a few days early (e.g. a
 * January-dated annual-upfront statement issued in late December).
 */
export function isFuturePeriod(
  year: number,
  month: number,
  now: Date = new Date(),
  graceMonths = 1,
): boolean {
  const nowIndex = now.getUTCFullYear() * 12 + now.getUTCMonth();
  return year * 12 + (month - 1) > nowIndex + graceMonths;
}

/** The calendar month immediately before `p` (MoM baseline selector). */
export function previousMonth(p: Period): Period {
  if (p.month === 1) return { year: p.year - 1, month: 12 };
  return { year: p.year, month: p.month - 1 };
}

/** Same month one quarter earlier (QoQ baseline). */
export function previousQuarter(p: Period): Period {
  return shiftMonths(p, -3);
}

/** Same month one year earlier (YoY baseline). */
export function previousYear(p: Period): Period {
  return { year: p.year - 1, month: p.month };
}

export function shiftMonths(p: Period, delta: number): Period {
  const idx = periodIndex(p) + delta;
  const year = Math.floor(idx / 12);
  const month = (idx % 12) + 1;
  return { year, month };
}

/** Parse a `YYYY-MM` string into a Period. */
export function parsePeriod(value: string): Period {
  const match = /^(\d{4})-(\d{1,2})$/.exec(value.trim());
  if (!match) throw new Error(`Invalid period "${value}", expected YYYY-MM`);
  const year = Number(match[1]);
  const month = Number(match[2]);
  return periodSchema.parse({ year, month });
}

/** Canonical machine key for a period (`YYYY-MM`). Used as an identifier. */
export function formatPeriod(p: Period): string {
  return `${p.year}-${String(p.month).padStart(2, '0')}`;
}

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * Human display label for a period — "April 2026". Accepts a Period or a
 * `YYYY-MM` string. Use this everywhere a period is shown to a user; reserve
 * `formatPeriod` (YYYY-MM) for machine keys.
 */
export function formatPeriodLong(input: Period | string | null | undefined): string {
  if (input == null) return '—';
  const p = typeof input === 'string' ? safeParse(input) : input;
  if (!p) return typeof input === 'string' ? input : '—';
  const name = MONTH_NAMES[p.month - 1];
  return name ? `${name} ${p.year}` : `${p.year}-${String(p.month).padStart(2, '0')}`;
}

function safeParse(value: string): Period | null {
  const match = /^(\d{4})-(\d{1,2})$/.exec(value.trim());
  if (!match) return null;
  return { year: Number(match[1]), month: Number(match[2]) };
}
